import React, { useEffect, useState, useRef } from 'react';
import { useCall } from './CallProvider';
import { useAuth } from '@/providers/AuthProvider';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Phone, 
  PhoneOff, 
  Mic, 
  MicOff, 
  Video, 
  VideoOff, 
  Minimize2, 
  Maximize2,
  Volume2,
  VolumeX,
  Settings,
  MonitorUp,
  MonitorOff
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useResponsive } from '@/hooks/useResponsive';

export function CallInterface() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const {
    currentCall,
    isInCall,
    isMuted,
    isVideoOn,
    remoteAudioMuted,
    isScreenSharing,
    isAdaptiveQualityEnabled,
    adaptiveVideoMode,
    qualityProfile,
    isCallMinimized,
    canScreenShare,
    localStream,
    remoteStream,
    callQuality,
    endCall,
    acceptCall,
    rejectCall,
    toggleMute,
    toggleVideo,
    toggleRemoteAudio,
    toggleScreenShare,
    toggleAdaptiveQuality,
    setQualityProfile,
    minimizeCall,
    maximizeCall
  } = useCall();

  const [callDuration, setCallDuration] = useState(0);
  const [isRinging, setIsRinging] = useState(false);
  const { isMobile } = useResponsive();
  const previousPathRef = useRef(pathname);

  // Refs for video elements to prevent flashing
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);

  // Calculate call duration
  useEffect(() => {
    if (currentCall?.status === 'connected') {
      const interval = setInterval(() => {
        const startTime = currentCall.startTime ? new Date(currentCall.startTime).getTime() : Date.now();
        const duration = Math.floor((Date.now() - startTime) / 1000);
        setCallDuration(duration);
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [currentCall?.status, currentCall?.startTime]);

  // Handle ringing animation
  useEffect(() => {
    if (currentCall?.status === 'ringing') {
      setIsRinging(true);
      const interval = setInterval(() => {
        setIsRinging(prev => !prev);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setIsRinging(false);
    }
  }, [currentCall?.status]);

  // Route transitions keep the call alive in a compact dock.
  useEffect(() => {
    if (!isInCall) {
      previousPathRef.current = pathname;
      return;
    }

    if (previousPathRef.current !== pathname && !isCallMinimized) {
      minimizeCall();
    }

    previousPathRef.current = pathname;
  }, [isCallMinimized, isInCall, minimizeCall, pathname]);

  // Set up local video stream - only update when stream changes
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      // Only set if not already set or if stream changed
      if (localVideoRef.current.srcObject !== localStream) {
        localVideoRef.current.srcObject = localStream;
      }

      // Safari/iOS sometimes needs an explicit play() after srcObject assignment
      localVideoRef.current.muted = true;
      const playPromise = localVideoRef.current.play();
      if (playPromise) {
        playPromise.catch(() => {
          // Ignore autoplay errors; user interaction will start playback.
        });
      }
    }
  }, [localStream]);

  // Set up remote video stream - only update when stream changes
  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      // Only set if not already set or if stream changed
      if (remoteVideoRef.current.srcObject !== remoteStream) {
        remoteVideoRef.current.srcObject = remoteStream;
      }

      const playPromise = remoteVideoRef.current.play();
      if (playPromise) {
        playPromise.catch(() => {
          // Ignore autoplay errors; user interaction will start playback.
        });
      }
    }
  }, [remoteStream]);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const qualityProfiles: Array<{ key: 'hd' | 'balanced' | 'data-saver'; label: string }> = [
    { key: 'hd', label: 'HD' },
    { key: 'balanced', label: 'Balanced' },
    { key: 'data-saver', label: 'Data Saver' },
  ];

  if (!isInCall || !currentCall) {
    return null;
  }

  // Minimized call interface
  if (isCallMinimized) {
    return (
      <div className={cn('fixed z-50', isMobile ? 'bottom-3 left-3 right-3' : 'bottom-4 left-4 max-w-[680px] w-[calc(100vw-2rem)]')}>
        <Card className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white border-0 shadow-2xl">
          <CardContent className={cn(isMobile ? 'p-2.5' : 'p-3')}>
            <div className="flex items-center gap-3">
              <Avatar className="w-9 h-9">
                <AvatarImage src={currentCall.receiver.avatar} />
                <AvatarFallback>{currentCall.receiver.name[0]}</AvatarFallback>
              </Avatar>

              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-sm truncate">{currentCall.receiver.name}</p>
                  <span className="rounded-full bg-white/15 px-2 py-0.5 text-[10px] uppercase tracking-wide">
                    Docked
                  </span>
                </div>
                <p className="text-xs text-blue-100">
                  {currentCall.status === 'connected' ? formatDuration(callDuration) : 'Connecting...'}
                </p>
              </div>

              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={maximizeCall}
                  className="w-8 h-8 p-0 hover:bg-white/20 text-white"
                  title="Expand call"
                >
                  <Maximize2 className="w-4 h-4" />
                </Button>

                <Button
                  size="sm"
                  variant="destructive"
                  onClick={endCall}
                  className="w-8 h-8 p-0 bg-red-500 hover:bg-red-600"
                  title="End call"
                >
                  <PhoneOff className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {currentCall.status === 'connected' && (
              <>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <Button
                    size="sm"
                    variant={isMuted ? 'destructive' : 'secondary'}
                    onClick={toggleMute}
                    className={cn('h-8 rounded-full text-xs', isMuted ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-slate-800/80 hover:bg-slate-700 text-white')}
                    title={isMuted ? 'Unmute microphone' : 'Mute microphone'}
                  >
                    {isMuted ? <MicOff className="w-3.5 h-3.5 mr-1" /> : <Mic className="w-3.5 h-3.5 mr-1" />}
                    Mic
                  </Button>

                  <Button
                    size="sm"
                    variant={!isVideoOn ? 'destructive' : 'secondary'}
                    onClick={toggleVideo}
                    className={cn('h-8 rounded-full text-xs', !isVideoOn ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-slate-800/80 hover:bg-slate-700 text-white')}
                    title={isVideoOn ? 'Turn camera off' : 'Turn camera on'}
                  >
                    {isVideoOn ? <Video className="w-3.5 h-3.5 mr-1" /> : <VideoOff className="w-3.5 h-3.5 mr-1" />}
                    Video
                  </Button>

                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={toggleScreenShare}
                    disabled={!canScreenShare}
                    className={cn(
                      'h-8 rounded-full text-xs text-white',
                      isScreenSharing ? 'bg-blue-600 hover:bg-blue-700' : 'bg-slate-800/80 hover:bg-slate-700',
                      !canScreenShare && 'opacity-50 cursor-not-allowed'
                    )}
                    title={!canScreenShare ? 'Screen share unavailable on this device/browser' : (isScreenSharing ? 'Stop sharing screen' : 'Share screen')}
                  >
                    {isScreenSharing ? <MonitorOff className="w-3.5 h-3.5 mr-1" /> : <MonitorUp className="w-3.5 h-3.5 mr-1" />}
                    Share
                  </Button>

                  <Button
                    size="sm"
                    variant={remoteAudioMuted ? 'destructive' : 'secondary'}
                    onClick={toggleRemoteAudio}
                    className={cn('h-8 rounded-full text-xs', remoteAudioMuted ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-slate-800/80 hover:bg-slate-700 text-white')}
                    title={remoteAudioMuted ? 'Unmute incoming call audio' : 'Mute incoming call audio'}
                  >
                    {remoteAudioMuted ? <VolumeX className="w-3.5 h-3.5 mr-1" /> : <Volume2 className="w-3.5 h-3.5 mr-1" />}
                    Audio
                  </Button>

                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={toggleAdaptiveQuality}
                    className={cn('h-8 rounded-full text-xs text-white', isAdaptiveQualityEnabled ? 'bg-cyan-600 hover:bg-cyan-700' : 'bg-slate-800/80 hover:bg-slate-700')}
                    title={isAdaptiveQualityEnabled ? 'Disable adaptive quality mode' : 'Enable adaptive quality mode'}
                  >
                    <Settings className="w-3.5 h-3.5 mr-1" />
                    Adaptive
                  </Button>
                </div>

                {currentCall.type === 'video' && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {qualityProfiles.map((profile) => {
                      const isActive = qualityProfile === profile.key;
                      return (
                        <Button
                          key={profile.key}
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            void setQualityProfile(profile.key);
                          }}
                          className={cn(
                            'h-7 rounded-full border text-[11px] font-semibold',
                            isActive
                              ? 'border-cyan-300 bg-cyan-500 text-white hover:bg-cyan-500'
                              : 'border-white/20 bg-slate-800/80 text-slate-200 hover:bg-slate-700'
                          )}
                          title={`Switch to ${profile.label} profile`}
                        >
                          {profile.label}
                        </Button>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Full call interface
  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 flex flex-col h-[100dvh] overflow-hidden">
      {/* Header */}
      <div className={cn('flex items-center justify-between text-white', isMobile ? 'px-3 pt-[max(env(safe-area-inset-top),0.75rem)] pb-3' : 'p-6')}>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            {currentCall.type === 'video' ? (
              <Video className="w-5 h-5 text-green-400" />
            ) : (
              <Phone className="w-5 h-5 text-green-400" />
            )}
            <span className="font-medium">
              {currentCall.type === 'video' ? 'Video Call' : 'Voice Call'}
            </span>
          </div>
          
          {currentCall.status === 'connected' && (
            <div className="flex items-center gap-2 text-green-400">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-sm">{formatDuration(callDuration)}</span>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={minimizeCall}
            className="text-white hover:bg-white/20"
            title="Minimize call"
          >
            <Minimize2 className="w-4 h-4" />
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={endCall}
            className="text-red-400 hover:bg-red-500/20"
            title="End call (force quit)"
          >
            <PhoneOff className="w-4 h-4" />
          </Button>
          
          {!isMobile && (
            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20"
              title="Settings"
            >
              <Settings className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Main call area */}
      <div className={cn('flex-1 flex items-center justify-center', isMobile ? 'px-3 pt-2 pb-1' : 'p-8')}>
        {currentCall.type === 'video' && currentCall.status === 'connected' ? (
          /* Video call layout */
          <div className="relative w-full h-full max-w-6xl">
            {/* Remote video */}
            <div className={cn('w-full h-full bg-gray-800 overflow-hidden relative', isMobile ? 'rounded-xl' : 'rounded-2xl')}>
              {remoteStream ? (
                <>
                  <video
                    ref={remoteVideoRef}
                    autoPlay
                    playsInline
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Beautiful blue overlay icons */}
                  <div className={cn('absolute flex flex-col', isMobile ? 'top-2 left-2 gap-2' : 'top-4 left-4 gap-3')}>
                    {/* Call info overlay */}
                    <div className={cn('bg-blue-500/80 backdrop-blur-md rounded-full flex items-center gap-2 shadow-lg', isMobile ? 'px-3 py-1.5' : 'px-4 py-2')}>
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                      <span className="text-white text-sm font-medium">{formatDuration(callDuration)}</span>
                    </div>
                    
                    {/* Participant name overlay */}
                    <div className={cn('bg-blue-500/80 backdrop-blur-md rounded-full flex items-center gap-2 shadow-lg', isMobile ? 'px-3 py-1.5' : 'px-4 py-2')}>
                      <Avatar className="w-6 h-6">
                        <AvatarImage src={currentCall.receiver.avatar} />
                        <AvatarFallback className="text-xs">{currentCall.receiver.name[0]}</AvatarFallback>
                      </Avatar>
                      <span className="text-white text-sm font-medium">{currentCall.receiver.name}</span>
                    </div>
                  </div>
                  
                  {/* Video quality indicator */}
                  <div className={cn('absolute', isMobile ? 'top-2 right-2' : 'top-4 right-4')}>
                    <div className={cn('bg-blue-500/80 backdrop-blur-md rounded-full shadow-lg', isMobile ? 'px-2.5 py-1' : 'px-3 py-1.5')}>
                      <span className="text-white text-xs font-medium">
                        {callQuality
                          ? `${callQuality.status.toUpperCase()} • ${callQuality.score}`
                          : 'Analyzing'}
                      </span>
                    </div>
                  </div>
                  
                  {/* Connection status indicator */}
                  <div className={cn('absolute', isMobile ? 'bottom-2 left-2' : 'bottom-4 left-4')}>
                    <div className={cn('bg-blue-500/80 backdrop-blur-md rounded-full flex items-center gap-2 shadow-lg', isMobile ? 'px-2.5 py-1' : 'px-3 py-1.5')}>
                      <div
                        className={cn(
                          'w-1.5 h-1.5 rounded-full',
                          callQuality?.status === 'poor'
                            ? 'bg-red-400'
                            : callQuality?.status === 'fair'
                              ? 'bg-amber-400'
                              : callQuality?.status === 'good'
                                ? 'bg-green-300'
                                : 'bg-green-400'
                        )}
                      ></div>
                      <span className="text-white text-xs">
                        {callQuality
                          ? `${callQuality.rttMs.toFixed(0)}ms RTT • ${callQuality.packetLossPercent.toFixed(1)}% loss`
                          : 'Measuring network...'}
                      </span>
                    </div>
                  </div>

                  {callQuality?.recommendations?.[0] && (
                    <div className={cn('absolute', isMobile ? 'bottom-10 left-2 right-2' : 'bottom-12 left-4 right-4')}>
                      <div className="bg-black/40 backdrop-blur-md rounded-lg px-3 py-2">
                        <span className="text-white text-xs">{callQuality.recommendations[0]}</span>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center">
                    <Avatar className={cn('mx-auto mb-4', isMobile ? 'w-24 h-24' : 'w-32 h-32')}>
                      <AvatarImage src={currentCall.receiver.avatar} />
                      <AvatarFallback className="text-4xl bg-gradient-to-r from-blue-500 to-indigo-500 text-white">
                        {currentCall.receiver.name[0]}
                      </AvatarFallback>
                    </Avatar>
                    <p className="text-white text-lg">Connecting video...</p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Local video (picture-in-picture) */}
            {isVideoOn && localStream && (
              <div className={cn('absolute bg-gray-800 rounded-xl overflow-hidden border-2 border-white/20 shadow-2xl', isMobile ? 'bottom-2 right-2 w-24 h-32' : 'top-4 right-4 w-48 h-36')}>
                <video
                  ref={localVideoRef}
                  autoPlay
                  muted
                  playsInline
                  className="w-full h-full object-cover"
                />
                {/* Local video label */}
                <div className="absolute bottom-2 left-2">
                  <div className="bg-blue-500/80 backdrop-blur-md px-2 py-1 rounded-full">
                    <span className="text-white text-xs font-medium">You</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Audio call or ringing layout */
          <div className="text-center px-3">
            <div className={cn(
              'relative transition-transform duration-1000',
              isMobile ? 'mb-5' : 'mb-8',
              isRinging && "scale-110"
            )}>
              <Avatar className={cn('mx-auto', isMobile ? 'w-32 h-32' : 'w-48 h-48')}>
                <AvatarImage src={currentCall.receiver.avatar} />
                <AvatarFallback className="text-6xl bg-gradient-to-r from-blue-500 to-indigo-500 text-white">
                  {currentCall.receiver.name[0]}
                </AvatarFallback>
              </Avatar>
              
              {/* Pulsing ring animation */}
              {currentCall.status === 'ringing' && (
                <>
                  <div className="absolute inset-0 rounded-full border-4 border-blue-400 animate-ping opacity-30"></div>
                  <div className="absolute inset-0 rounded-full border-4 border-blue-400 animate-ping opacity-20 animation-delay-300"></div>
                </>
              )}
            </div>
            
            <h2 className={cn('font-bold text-white mb-2', isMobile ? 'text-2xl' : 'text-3xl')}>{currentCall.receiver.name}</h2>
            <p className={cn('text-blue-200', isMobile ? 'text-base mb-5' : 'text-xl mb-8')}>
              {currentCall.status === 'ringing' ? 'Calling...' : 
               currentCall.status === 'connected' ? 'Connected' : 'Connecting...'}
            </p>
          </div>
        )}
      </div>

      {/* Call controls */}
      <div className={cn('w-full', isMobile ? 'px-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] pt-2' : 'p-8')}>
        {isMobile && (
          <div className="mb-2 rounded-lg border border-white/15 bg-black/20 px-3 py-2 text-center text-xs text-blue-100">
            {currentCall.type === 'video'
              ? (isScreenSharing ? 'Screen sharing is live' : 'Video call controls optimized for mobile')
              : 'Voice call mode active. Turn on camera or share screen anytime.'}
          </div>
        )}

        {currentCall.status === 'connected' && callQuality && (
          <div className="mb-2 rounded-lg border border-white/15 bg-black/20 px-3 py-2 text-center text-xs text-blue-100">
            {`RTT ${callQuality.rttMs.toFixed(0)}ms • Loss ${callQuality.packetLossPercent.toFixed(1)}% • Uplink ${callQuality.bitrateKbps.toFixed(0)}kbps • Adaptive ${isAdaptiveQualityEnabled ? (adaptiveVideoMode === 'auto-audio-only' ? 'Audio-safe' : 'On') : 'Off'}`}
          </div>
        )}

        {currentCall.status === 'connected' && (
          <div className="mb-3 rounded-lg border border-white/15 bg-black/20 p-2">
            <div className="mb-2 text-center text-[11px] uppercase tracking-wide text-blue-100/90">
              Quality Profile
            </div>
            <div className="flex items-center justify-center gap-2">
              {qualityProfiles.map((profile) => {
                const isActive = qualityProfile === profile.key;
                return (
                  <Button
                    key={profile.key}
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      void setQualityProfile(profile.key);
                    }}
                    className={cn(
                      'min-w-[90px] rounded-full border text-xs font-semibold transition-colors',
                      isMobile ? 'h-8 px-3' : 'h-9 px-4',
                      isActive
                        ? 'border-cyan-300 bg-cyan-500 text-white hover:bg-cyan-500'
                        : 'border-white/20 bg-slate-800/80 text-slate-200 hover:bg-slate-700'
                    )}
                    title={`Switch to ${profile.label} profile`}
                  >
                    {profile.label}
                  </Button>
                );
              })}
            </div>
          </div>
        )}

        <div className={cn('flex items-center justify-center', isMobile ? 'gap-2 flex-wrap' : 'gap-4')}>
          {/* Mute button */}
          <div className="flex flex-col items-center gap-1">
            <Button
              size="lg"
              variant={isMuted ? 'destructive' : 'secondary'}
              onClick={toggleMute}
              className={cn(
                isMobile ? 'w-12 h-12 rounded-full' : 'w-16 h-16 rounded-full',
                isMuted ? 'bg-red-500 hover:bg-red-600' : 'bg-gray-700 hover:bg-gray-600 text-white'
              )}
            >
              {isMuted ? <MicOff className={cn(isMobile ? 'w-5 h-5' : 'w-6 h-6')} /> : <Mic className={cn(isMobile ? 'w-5 h-5' : 'w-6 h-6')} />}
            </Button>
            {isMobile && <span className="text-[10px] text-blue-100">{isMuted ? 'Unmute' : 'Mute'}</span>}
          </div>

          {/* Video toggle (allows upgrading audio calls to video) */}
          {currentCall.status === 'connected' && (
            <div className="flex flex-col items-center gap-1">
              <Button
                size="lg"
                variant={!isVideoOn ? 'destructive' : 'secondary'}
                onClick={toggleVideo}
                className={cn(
                  isMobile ? 'w-12 h-12 rounded-full' : 'w-16 h-16 rounded-full',
                  !isVideoOn ? 'bg-red-500 hover:bg-red-600' : 'bg-gray-700 hover:bg-gray-600 text-white'
                )}
              >
                {isVideoOn ? <Video className={cn(isMobile ? 'w-5 h-5' : 'w-6 h-6')} /> : <VideoOff className={cn(isMobile ? 'w-5 h-5' : 'w-6 h-6')} />}
              </Button>
              {isMobile && <span className="text-[10px] text-blue-100">{isVideoOn ? 'Camera' : 'Video off'}</span>}
            </div>
          )}

          {/* Screen share toggle (available for connected calls) */}
          {currentCall.status === 'connected' && (
            <div className="flex flex-col items-center gap-1">
              <Button
                size="lg"
                variant="secondary"
                onClick={toggleScreenShare}
                disabled={!canScreenShare}
                className={cn(
                  isMobile ? 'w-12 h-12 rounded-full text-white' : 'w-16 h-16 rounded-full text-white',
                  isScreenSharing ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-700 hover:bg-gray-600',
                  !canScreenShare && 'opacity-50 cursor-not-allowed'
                )}
                title={!canScreenShare ? 'Screen share unavailable on this device/browser' : (isScreenSharing ? 'Stop sharing screen' : 'Share screen')}
              >
                {isScreenSharing ? <MonitorOff className={cn(isMobile ? 'w-5 h-5' : 'w-6 h-6')} /> : <MonitorUp className={cn(isMobile ? 'w-5 h-5' : 'w-6 h-6')} />}
              </Button>
              {isMobile && (
                <span className="text-[10px] text-blue-100">
                  {!canScreenShare ? 'No share' : (isScreenSharing ? 'Stop share' : 'Share')}
                </span>
              )}
            </div>
          )}

          {/* Remote audio playback toggle */}
          {currentCall.status === 'connected' && (
            <div className="flex flex-col items-center gap-1">
              <Button
                size="lg"
                variant={remoteAudioMuted ? 'destructive' : 'secondary'}
                onClick={toggleRemoteAudio}
                className={cn(
                  isMobile ? 'w-12 h-12 rounded-full' : 'w-16 h-16 rounded-full',
                  remoteAudioMuted ? 'bg-red-500 hover:bg-red-600' : 'bg-gray-700 hover:bg-gray-600 text-white'
                )}
                title={remoteAudioMuted ? 'Unmute incoming call audio' : 'Mute incoming call audio'}
              >
                {remoteAudioMuted ? <VolumeX className={cn(isMobile ? 'w-5 h-5' : 'w-6 h-6')} /> : <Volume2 className={cn(isMobile ? 'w-5 h-5' : 'w-6 h-6')} />}
              </Button>
              {isMobile && <span className="text-[10px] text-blue-100">{remoteAudioMuted ? 'Unmute peer' : 'Mute peer'}</span>}
            </div>
          )}

          {/* Adaptive quality mode toggle */}
          {currentCall.status === 'connected' && currentCall.type === 'video' && (
            <div className="flex flex-col items-center gap-1">
              <Button
                size="lg"
                variant="secondary"
                onClick={toggleAdaptiveQuality}
                className={cn(
                  isMobile ? 'w-12 h-12 rounded-full text-white' : 'w-16 h-16 rounded-full text-white',
                  isAdaptiveQualityEnabled ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-700 hover:bg-gray-600'
                )}
                title={isAdaptiveQualityEnabled ? 'Disable adaptive quality mode' : 'Enable adaptive quality mode'}
              >
                <Settings className={cn(isMobile ? 'w-5 h-5' : 'w-6 h-6')} />
              </Button>
              {isMobile && (
                <span className="text-[10px] text-blue-100">{isAdaptiveQualityEnabled ? 'Adaptive on' : 'Adaptive off'}</span>
              )}
            </div>
          )}

          <div className="flex flex-col items-center gap-1">
            <Button
              size="lg"
              variant="destructive"
              onClick={endCall}
              className={cn('rounded-full bg-red-500 hover:bg-red-600', isMobile ? 'w-14 h-12' : 'w-20 h-16')}
            >
              <PhoneOff className={cn(isMobile ? 'w-5 h-5' : 'w-6 h-6')} />
            </Button>
            {isMobile && <span className="text-[10px] text-red-200">End</span>}
          </div>

          {/* Accept/Reject (if receiving call) */}
          {currentCall.status === 'ringing' && currentCall.caller.id !== user?.uid && (
            <>
              <Button
                size="lg"
                variant="secondary"
                onClick={() => acceptCall(currentCall.id)}
                className={cn('rounded-full bg-green-500 hover:bg-green-600 text-white', isMobile ? 'w-12 h-12' : 'w-16 h-16')}
              >
                <Phone className={cn(isMobile ? 'w-5 h-5' : 'w-6 h-6')} />
              </Button>
              
              <Button
                size="lg"
                variant="destructive"
                onClick={() => rejectCall(currentCall.id)}
                className={cn('rounded-full bg-red-500 hover:bg-red-600', isMobile ? 'w-12 h-12' : 'w-16 h-16')}
              >
                <PhoneOff className={cn(isMobile ? 'w-5 h-5' : 'w-6 h-6')} />
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
